import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom, of, switchMap, tap } from 'rxjs';

import { ConfigService } from './config.service';
import { MessageResponse, TokenResponse, UserLoginRequest, UserProfile, UserRegistrationRequest } from '../models/auth.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = '';
  private readonly tokenKey = 'anyspace_access_token';
  private readonly currentUserSubject = new BehaviorSubject<UserProfile | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();

  constructor(private http: HttpClient, private configService: ConfigService) {}

  async initializeService(): Promise<void> {
    const config = await firstValueFrom(this.configService.loadConfig());
    this.apiUrl = config.authenticationEndpoint;
  }

  register(payload: UserRegistrationRequest): Observable<UserProfile> {
    return this.http.post<UserProfile>(`${this.apiUrl}/register`, payload);
  }

  login(payload: UserLoginRequest): Observable<UserProfile> {
    return this.http.post<TokenResponse>(`${this.apiUrl}/login`, payload).pipe(
      tap((response) => localStorage.setItem(this.tokenKey, response.access_token)),
      switchMap(() => this.fetchCurrentUser())
    );
  }

  fetchCurrentUser(): Observable<UserProfile> {
    const token = this.getToken();
    if (!token) {
      this.currentUserSubject.next(null);
      return of(null as unknown as UserProfile);
    }

    return this.http.get<UserProfile>(`${this.apiUrl}/me`, { headers: this.authHeaders(token) }).pipe(
      tap((profile) => this.currentUserSubject.next(profile))
    );
  }

  forgotPassword(email: string): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(`${this.apiUrl}/forgot-password`, { email });
  }

  resetPassword(token: string, password: string): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(`${this.apiUrl}/reset-password`, { token, password });
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    this.currentUserSubject.next(null);
  }

  hasToken(): boolean {
    return !!this.getToken();
  }

  getCurrentUserSnapshot(): UserProfile | null {
    return this.currentUserSubject.value;
  }

  private getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  private authHeaders(token: string): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }
}
